import React, { useState, useRef } from "react";
import Alert from '../common/Alert';

const handlePaste = (e) => {
  e.preventDefault();
};

// Reusable component for input fields
const InputField = ({ label, id, name, type, value, handleInputChange, placeholder, pattern, onPaste }) => (
  <div>
    <label htmlFor={id}>{label} *</label>
    <input
      type={type}
      id={id}
      name={name}
      value={value}
      onChange={handleInputChange}
      onBlur={handleInputChange}
      onPaste={onPaste}
      required
      placeholder={placeholder}
      pattern={pattern}
    />
  </div>
);

const BasicDetailsForm = ({ formData, handleInputChange, handleSubmit, handleExistingUser }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [pinApiError, setPinApiError] = useState(null);
  const confirmEmailRef = useRef(null);
  const confirmPhoneRef = useRef(null);
  const confirmAadharRef = useRef(null);
  const previousCityRef = useRef('');
  const [cities, setCities] = useState([]);

  const validateForm = () => {
    const newErrors = {};
    if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = "Email addresses do not match.";
    }
    if (formData.phone !== formData.confirmPhone) {
      newErrors.confirmPhone = "Phone numbers do not match.";
    }
    if (formData.aadharNumber !== formData.confirmAadhar) {
      newErrors.confirmAadhar = "Aadhar numbers do not match.";
    }
    setErrors(newErrors);

    // Navigate to the first error field
    if (newErrors.confirmEmail && confirmEmailRef.current) {
      confirmEmailRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (newErrors.confirmPhone && confirmPhoneRef.current) {
      confirmPhoneRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (newErrors.confirmAadhar && confirmAadharRef.current) {
      confirmAadharRef.current.scrollIntoView({ behavior: "smooth" });
    }

    return Object.keys(newErrors).length === 0;
  };

  // Autofill city/state based on pincode using external API
  const pinDebounceRef = useRef(null);

  const handlePinCodeChange = (e) => {
    let pin = e.target.value.replace(/\D/g, '').slice(0, 6); // digits only, max 6
    // Allow only numeric input with max 6 digits
    if (!/^\d{0,6}$/.test(pin)) return;

    // Update formData (must preserve 6-digit input)
    handleInputChange({
      target: { name: 'pinCode', value: pin }
    });

    // Don't proceed if not 6 digits
    if (pin.length !== 6) {
      setCities([]);
      handleInputChange({ target: { name: 'district', value: '' } });
      handleInputChange({ target: { name: 'state', value: '' } });
      setPinApiError(null);
      return;
    }

    // Debounce the API call
    if (pinDebounceRef.current) {
      clearTimeout(pinDebounceRef.current);
    }

    pinDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await res.json();

        if (data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
          const postOffice = data[0].PostOffice[0];
          setCities(data[0].PostOffice.map(po => po.Name)); // Update cities list
          handleInputChange({ target: { name: 'district', value: postOffice.District } });
          handleInputChange({ target: { name: 'state', value: postOffice.State } });
          setPinApiError(null);
        } else {
          handleInputChange({ target: { name: 'district', value: '' } });
          handleInputChange({ target: { name: 'state', value: '' } });
          setPinApiError("Invalid PIN code or not found.");
        }
      } catch (err) {
        handleInputChange({ target: { name: 'district', value: '' } });
        handleInputChange({ target: { name: 'state', value: '' } });
        setPinApiError("Failed to fetch district/state for this PIN code.");
      }
    }, 400);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await handleSubmit(e);
      } catch (error) {
        console.error('Submission failed:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to submit basic details';
        setAlert({ type: 'error', message: errorMessage });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
      <form onSubmit={onSubmit} className="form-section">
        <div className="form-actions" style={{ marginBottom: "1rem", marginTop: 0 }}>
          <button onClick={handleExistingUser} className="btn">
            Already Registered? Click Here
          </button>
        </div>

        <h2>Basic Details of Student:</h2>
        <p>
          Please fill in your personal information to begin the registration
          process.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              placeholder="Enter your first name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              placeholder="Enter your last name"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <InputField
              label="Email Address"
              id="email"
              name="email"
              type="password"
              value={formData.email}
              handleInputChange={handleInputChange}
              placeholder="Enter your email address"
              onPaste={handlePaste}
            />
          </div>
          <div className="form-group" ref={confirmEmailRef}>
            <InputField
              label="Confirm Email Address"
              id="confirmEmail"
              name="confirmEmail"
              type="email"
              value={formData.confirmEmail}
              handleInputChange={handleInputChange}
              placeholder="Re-enter your email address"
              onPaste={handlePaste}
            />
            {errors.confirmEmail && (
              <p className="error" style={{ marginBottom: '0rem' }}>{errors.confirmEmail}</p>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <InputField
              label="Phone Number (10 digits)"
              id="phone"
              name="phone"
              type="password"
              value={formData.phone}
              handleInputChange={handleInputChange}
              placeholder="Enter 10-digit phone number"
              pattern="[0-9]{10}"
              onPaste={handlePaste}
            />
          </div>
          <div className="form-group" ref={confirmPhoneRef}>
            <InputField
              label="Confirm Phone Number (10 digits)"
              id="confirmPhone"
              name="confirmPhone"
              type="tel"
              value={formData.confirmPhone}
              handleInputChange={handleInputChange}
              placeholder="Re-enter 10-digit phone number"
              pattern="[0-9]{10}"
              onPaste={handlePaste}
            />
            {errors.confirmPhone && (
              <p className="error" style={{ marginBottom: '0rem' }}>{errors.confirmPhone}</p>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <InputField
              label="Aadhar Number"
              id="aadharNumber"
              name="aadharNumber"
              type="password"
              value={formData.aadharNumber}
              handleInputChange={handleInputChange}
              placeholder="Enter 12-digit Aadhar number"
              pattern="[0-9]{12}"
              onPaste={handlePaste}
            />
          </div>
          <div className="form-group" ref={confirmAadharRef}>
            <InputField
              label="Confirm Aadhar Number"
              id="confirmAadhar"
              name="confirmAadhar"
              type="text"
              value={formData.confirmAadhar}
              handleInputChange={handleInputChange}
              placeholder="Re-enter 12-digit Aadhar number"
              pattern="[0-9]{12}"
              onPaste={handlePaste}
            />
            {errors.confirmAadhar && (
              <p className="error" style={{ marginBottom: '0rem' }}>{errors.confirmAadhar}</p>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              placeholder="Enter your complete address"
              rows="3"
            />
          </div>
        </div>

        <div className="form-row">
          {/* PIN code input */}
          <div className="form-group">
            <label htmlFor="pinCode">PIN Code *</label>
            <input
              type="text"
              id="pinCode"
              name="pinCode"
              value={formData.pinCode}
              onChange={handlePinCodeChange}
              required
              pattern="[0-9]{6}"
              placeholder="Enter 6-digit PIN code"
            // maxLength={6}
            />
            {pinApiError && (
              <p className="error" style={{ marginBottom: '0rem' }}>{pinApiError}</p>
            )}
          </div>

          {/* City (auto-filled, disabled) */}
          <div className="form-group">
            <label htmlFor="city">City/Town/Village *</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required
              disabled={!cities.length}
              placeholder={!cities.length ? "First Enter Pincode" : "Enter your City/Town/Village"}
              list="cities"
              onFocus={(e) => {
                previousCityRef.current = e.target.value;
                handleInputChange({ target: { name: 'city', value: '' } });
              }}
              onBlur={(e) => {
                if (!e.target.value.trim() && previousCityRef.current) {
                  handleInputChange({ target: { name: 'city', value: previousCityRef.current } });
                }
              }}
            />
            {cities.length > 0 && (
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#dc2626', fontStyle: 'italic', display: 'flex', alignItems: 'flex-start', gap: '0.25rem' }}>
                <svg style={{ width: '0.85rem', height: '0.85rem', flexShrink: 0, marginTop: '0.05rem' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
                </svg>
                If no option matches, you can type your own City/Town/Village name.
              </p>
            )}
          </div>

          <datalist id="cities">
            {cities.map((city, index) => (
              <option key={index} value={city}>{city}</option>
            ))}
          </datalist>


          {/* District (auto-filled, disabled) */}
          <div className="form-group">
            <label htmlFor="district">District *</label>
            <input
              type="text"
              id="distric"
              name="district"
              value={formData.district}
              disabled
              required
              placeholder="Auto-filled from PIN code"
            />
          </div>

          {/* State (auto-filled, disabled) */}
          <div className="form-group">
            <label htmlFor="state">State *</label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              disabled
              required
              placeholder="Auto-filled from PIN code"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-small"></span>
                Processing...
              </>
            ) : (
              'Proceed to Payment'
            )}
          </button>
        </div>
      </form>
    </>
  );
};

export default BasicDetailsForm;